import { useState } from "react";
import { Phone, MapPin, User, GripVertical } from "lucide-react";
import { STATUS_OPTIONS } from "../poolConstants";

const DOT = {
  neu: "bg-blue-500", vorgemerkt: "bg-violet-500", kontaktiert: "bg-amber-500",
  qualifiziert: "bg-green-600", kein_interesse: "bg-muted-foreground", ausgeschlossen: "bg-red-500",
};

const BAR = {
  neu: "bg-blue-500", vorgemerkt: "bg-violet-500", kontaktiert: "bg-amber-500",
  qualifiziert: "bg-green-600", kein_interesse: "bg-slate-400", ausgeschlossen: "bg-red-500",
};

export function PraxisKanban({ leads, onChangeStatus, onEdit, deletedMode = false }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const handleDrop = (e, status) => {
    e.preventDefault();
    setOverCol(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    const lead = leads.find((l) => l.id === id);
    if (lead && lead.status !== status) onChangeStatus(lead, status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 p-3" data-testid="praxis-kanban">
      {STATUS_OPTIONS.map((opt) => {
        const items = leads.filter((l) => (l.status || "neu") === opt.value);
        return (
          <div
            key={opt.value}
            onDragOver={(e) => { if (!deletedMode) { e.preventDefault(); setOverCol(opt.value); } }}
            onDragLeave={() => setOverCol((c) => (c === opt.value ? null : c))}
            onDrop={(e) => !deletedMode && handleDrop(e, opt.value)}
            className={`w-72 shrink-0 rounded-xl border bg-muted/30 flex flex-col transition-colors ${overCol === opt.value ? "border-primary bg-primary/5" : "border-border"}`}
            data-testid={`praxis-kanban-col-${opt.value}`}
          >
            <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${DOT[opt.value] || "bg-muted-foreground"}`} />
                <span className="font-semibold text-sm">{opt.label}</span>
              </div>
              <span className="text-xs font-medium text-muted-foreground bg-background rounded-full px-2 py-0.5" data-testid={`praxis-kanban-count-${opt.value}`}>{items.length}</span>
            </div>
            <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[68vh]">
              {items.map((l) => {
                const status = l.status || "neu";
                const tel = (l.phone || "").replace(/[^0-9+]/g, "");
                return (
                <div
                  key={l.id}
                  draggable={!deletedMode}
                  onDragStart={(e) => { e.dataTransfer.setData("text/plain", l.id); setDragId(l.id); }}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onEdit && onEdit(l)}
                  className={`group relative overflow-hidden rounded-lg border border-border bg-card p-2.5 pl-3.5 cursor-pointer hover:shadow-md hover:border-primary/40 transition-all ${dragId === l.id ? "opacity-50" : ""}`}
                  data-testid={`praxis-kanban-card-${l.id}`}
                >
                  <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${BAR[status] || "bg-slate-400"}`} aria-hidden="true" />
                  <div className="flex items-start gap-1.5">
                    <p className="font-medium text-sm truncate flex-1">{l.practice_name}</p>
                    {!deletedMode && <GripVertical className="w-4 h-4 text-muted-foreground/30 group-hover:text-muted-foreground shrink-0" />}
                  </div>
                  <div className="mt-1.5 space-y-1 text-xs text-muted-foreground">
                    {l.person_name && <p className="inline-flex items-center gap-1 truncate"><User className="w-3 h-3" />{l.person_name}</p>}
                    {(l.city || l.zip) && <p className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{[l.zip, l.city].filter(Boolean).join(" ")}</p>}
                    {l.phone && (
                      <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 truncate flex-1"><Phone className="w-3 h-3" />{l.phone}</span>
                        {tel && (
                          <a
                            href={`tel:${tel}`}
                            onClick={(e) => e.stopPropagation()}
                            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 transition-colors"
                            title={`Anrufen: ${l.phone}`}
                            data-testid={`praxis-kanban-call-${l.id}`}
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
              {items.length === 0 && (
                <div className="text-center text-xs text-muted-foreground py-6 border border-dashed border-border rounded-lg">—</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
